import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Snackbar, Tooltip, Typography } from '@mui/material';
import {
  AdminPanelSettings,
  Badge as BadgeIcon,
  CheckCircle,
  Close,
  Folder,
  History,
  LocationOn,
  Login,
  Mail,
  MoreVert,
  Phone,
  VpnKey,
  Warning,
  Delete,
} from '@mui/icons-material';
import Moment from 'moment';
import { useMutation, useQuery } from '@apollo/client/react';
import { useConfig } from '../../components/Configuration';
import { formatDateOnly } from '../../Formatters';
import { GET_BOUNCED, GET_LAST_RESET_PASSWORD_URL, REMOVE_BOUNCE } from '../user.graphql';
import { Block } from '@mui/icons-material';
import { tokens } from './tokens';
import { Config } from '../../config';
import DeleteUsers from '../overview/DeleteUsers';

interface HeroUser {
  id: number;
  loginId?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  lastLoggedIn?: string | null;
  createdAt?: string | null;
  isActive: boolean;
  isAdminuserOnActiveLicense: boolean;
  address?: { city?: string | null } | null;
}

interface Props {
  user?: HeroUser | null;
  isActive: boolean;
  totalAccesses: number;
  licenseGroupCount: number;
  adminLicenseCount: number;
}

function relativeFromNow(iso?: string | null): string {
  if (!iso) return '–';
  return Moment.utc(iso).fromNow();
}

// lastLoggedIn comes from the API as a UTC timestamp; render it in Norwegian local time.
function formatLocalDate(iso?: string | null): string {
  if (!iso) return '';
  return Moment.utc(iso).local().format('DD.MM.YYYY HH:mm');
}

function yearsSince(iso?: string | null): string {
  if (!iso) return '–';
  const years = Math.floor(Moment().diff(Moment(iso), 'years', true));
  return `${years} år`;
}

function getLoginAsUserUrl(config: Config | undefined, user?: HeroUser | null) {
  if (!config || !user) return undefined;
  if (config.featureToggleChangeUserNewPortal) {
    return `${config.portalUrl}/impersonate/${user.loginId}`;
  } else {
    return `${config.bksUrl}/Administration/LoginAsAnotherUser/${user.loginId}`
  }
}

export function Hero({ user, isActive, totalAccesses, licenseGroupCount, adminLicenseCount }: Props) {
  const config = useConfig();
  const navigate = useNavigate();
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const wasDeleted = useRef(false);

  const { data: bouncedData } = useQuery<{ bounced: { id: number; isBounced: boolean; reason: string } }>(GET_BOUNCED, {
    variables: { id: user?.id ?? 0 },
    skip: !user,
  });
  const isBounced = bouncedData?.bounced?.isBounced ?? false;
  const bounceReason = bouncedData?.bounced?.reason ?? '';

  const [removeBounce] = useMutation<any, any>(REMOVE_BOUNCE, { variables: { input: { id: user?.id ?? 0 } } });
  const canRemoveBounce = !!config?.featureToggleRemoveBounce;

  const { data: resetUrlData } = useQuery<{ url: string | null }>(GET_LAST_RESET_PASSWORD_URL, {
    variables: { id: user?.id ?? 0 },
    skip: !user,
  });
  const resetUrl = resetUrlData?.url || null;

  const initials = useMemo(() => {
    const first = (user?.firstName?.[0] ?? '').toUpperCase();
    const last = (user?.lastName?.[0] ?? '').toUpperCase();
    return `${first}${last}` || '?';
  }, [user?.firstName, user?.lastName]);


  const loginAsUserUrl = getLoginAsUserUrl(config, user);

  const handleCopyResetUrl = async () => {
    if (!resetUrl || !navigator.clipboard) return;
    await navigator.clipboard.writeText(resetUrl);
    setCopySnackbarOpen(true);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: `${tokens.radius}px`,
        background: theme => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        color: '#fff',
        padding: { xs: '20px', sm: '24px 24px', md: '28px 32px' },
        boxShadow: tokens.shadow,
      }}>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: -80,
          top: -80,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', display: 'flex', gap: { xs: 2, sm: 3 }, alignItems: 'center' }}>
        <Avatar
          sx={{
            width: { xs: 56, sm: 72, md: 88 },
            height: { xs: 56, sm: 72, md: 88 },
            fontSize: '36%',
            fontWeight: 500,
            flexShrink: 0,
            background: theme => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: '#fff',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
          }}>
          <Typography component="span" sx={{ fontSize: { xs: 20, sm: 26, md: 32 }, fontWeight: 500, lineHeight: 1 }}>
            {initials}
          </Typography>
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {isActive ? (
                <BadgePill tone="success" icon={<CheckCircle sx={{ fontSize: 12 }} />}>
                  Aktiv
                </BadgePill>
              ) : (
                <BadgePill tone="error" icon={<Close sx={{ fontSize: 12 }} />}>
                  Inaktiv
                </BadgePill>
              )}
              {user?.isAdminuserOnActiveLicense && (
                <BadgePill tone="primary" icon={<AdminPanelSettings sx={{ fontSize: 12 }} />}>
                  Administrator
                </BadgePill>
              )}
              {isBounced && (
                <Tooltip title={bounceReason || 'E-post-adressen er blokkert'} placement="top">
                  <span>
                    <BadgePill tone="warn" icon={<Warning sx={{ fontSize: 12 }} />}>
                      E-post bouncer
                    </BadgePill>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Typography
            component="h1"
            sx={{
              m: 0,
              fontSize: { xs: 20, sm: 24, md: 30 },
              fontWeight: 400,
              letterSpacing: '-0.01em',
              lineHeight: 1.15,
              wordBreak: 'break-word',
            }}>
            {user?.displayName || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || '—'}
          </Typography>

          <Box
            sx={{
              mt: 0.75,
              display: 'flex',
              gap: { xs: 1, sm: 2.25 },
              flexWrap: 'wrap',
              alignItems: 'center',
              fontSize: { xs: 12, sm: 13 },
              color: 'rgba(255,255,255,0.85)',
              minWidth: 0,
            }}>
            {user?.email && <ContactChip icon={<Mail sx={{ fontSize: 14 }} />}>{user.email}</ContactChip>}
            {user?.phone && <ContactChip icon={<Phone sx={{ fontSize: 14 }} />}>{user.phone}</ContactChip>}
            {user?.address?.city && <ContactChip icon={<LocationOn sx={{ fontSize: 14 }} />}>{user.address.city}</ContactChip>}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: { xs: 2, md: 3 },
          mt: { xs: '16px', md: '22px' },
          pt: { xs: '16px', md: '20px' },
          borderTop: '1px solid rgba(255,255,255,0.18)',
        }}>
        <Stat
          icon={<Folder sx={{ fontSize: 13 }} />}
          label="Tilganger"
          value={String(totalAccesses)}
          sub={`på ${licenseGroupCount} lisens${licenseGroupCount === 1 ? '' : 'er'}`}
        />
        <Stat
          icon={<AdminPanelSettings sx={{ fontSize: 13 }} />}
          label="Administrerer"
          value={String(adminLicenseCount)}
          sub={adminLicenseCount === 1 ? 'lisens' : 'lisenser'}
        />
        <Stat
          icon={<History sx={{ fontSize: 13 }} />}
          label="Sist pålogget"
          value={relativeFromNow(user?.lastLoggedIn)}
          sub={formatLocalDate(user?.lastLoggedIn)}
        />
        <Stat
          icon={<BadgeIcon sx={{ fontSize: 13 }} />}
          label="Medlem i"
          value={yearsSince(user?.createdAt)}
          sub={user?.createdAt ? formatDateOnly(user.createdAt) : ''}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          gap: 1.25,
          flexWrap: 'wrap',
          mt: { xs: '16px', md: '20px' },
          pt: { xs: '16px', md: '20px' },
          borderTop: '1px solid rgba(255,255,255,0.18)',
        }}>
        <Button
          component="a"
          href={loginAsUserUrl}
          target="_blank"
          rel="noreferrer"
          disabled={!loginAsUserUrl}
          startIcon={<Login sx={{ fontSize: 16 }} />}
          sx={{
            height: 36,
            px: 2,
            borderRadius: '6px',
            background: 'rgba(255,255,255,0.95)',
            color: tokens.primaryDark,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textDecoration: 'none',
            '&:hover': { background: '#fff', textDecoration: 'none' },
          }}>
          Logg inn som bruker
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={(e) => setMoreAnchor(e.currentTarget)}
          startIcon={<MoreVert sx={{ fontSize: 14 }} />}
          sx={{
            height: 36,
            px: 1.75,
            borderRadius: '6px',
            background: 'transparent',
            color: 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(255,255,255,0.25)',
            fontSize: 12,
            fontWeight: 500,
            letterSpacing: '0.04em',
            '&:hover': { background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.4)' },
          }}>
          Flere valg
        </Button>
        <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
          {resetUrl && (
            <MenuItem
              onClick={() => {
                handleCopyResetUrl();
                setMoreAnchor(null);
              }}>
              <ListItemIcon>
                <VpnKey fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Kopiér lenke til nytt passord" />
            </MenuItem>
          )}
          {isBounced && (
            <MenuItem
              disabled={!canRemoveBounce}
              onClick={() => {
                removeBounce();
                setMoreAnchor(null);
              }}>
              <ListItemIcon>
                <Block fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Fjern e-post-blokkering"
                slotProps={{ secondary: { sx: { fontSize: 11, whiteSpace: 'normal' } } }}
              />
            </MenuItem>
          )}
          <MenuItem
            disabled={!user || user.isAdminuserOnActiveLicense}
            title={user?.isAdminuserOnActiveLicense ? 'Brukere som administrerer aktive lisenser kan ikke slettes.' : undefined}
            onClick={() => {
              setDeleteOpen(true);
              setMoreAnchor(null);
            }}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Slett bruker" slotProps={{ primary: { sx: { color: 'error.main' } } }} />
          </MenuItem>
        </Menu>

        {deleteOpen && user && (
          <DeleteUsers
            open={deleteOpen}
            onClose={() => {
              setDeleteOpen(false);
              if (wasDeleted.current) navigate('/user');
            }}
            userId={[user.id]}
            users={[{ id: user.id, displayName: user.displayName ?? '' }]}
            setSelected={() => { }}
            removeFromList={() => {
              wasDeleted.current = true;
            }}
          />
        )}
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Lenken ble kopiert"
        action={
          <IconButton color="inherit" size="small" onClick={() => setCopySnackbarOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}

function ContactChip({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
      {icon}
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {children}
      </Box>
    </Box>
  );
}

type Tone = 'success' | 'primary' | 'warn' | 'error';

function BadgePill({ tone, icon, children }: { tone: Tone; icon: React.ReactNode; children: React.ReactNode }) {
  const palette: Record<Tone, { bg: string; fg: string }> = {
    success: { bg: tokens.successTint, fg: '#1f6d44' },
    primary: { bg: tokens.primaryTint, fg: tokens.primaryDark },
    warn: { bg: tokens.warnTint, fg: '#9b5b07' },
    error: { bg: tokens.errorTint, fg: '#a13127' },
  };
  const { bg, fg } = palette[tone];
  return (
    <Chip
      size="small"
      icon={<Box sx={{ display: 'inline-flex', color: fg, ml: '6px !important', mr: '-4px !important' }}>{icon}</Box>}
      label={children}
      sx={{
        height: 20,
        background: bg,
        color: fg,
        borderRadius: 999,
        px: 0.5,
        '& .MuiChip-label': {
          px: 0.75,
          fontSize: 11,
          fontWeight: 500,
          lineHeight: 1,
        },
      }}
    />
  );
}

function Stat({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.7)',
          fontWeight: 500,
        }}>
        {icon}
        {label}
      </Box>
      <Typography
        sx={{
          fontSize: { xs: 18, md: 22 },
          fontWeight: 500,
          color: theme => theme.palette.primary.contrastText,
          lineHeight: 1.2,
          mt: 0.25,
          wordBreak: 'break-word',
        }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: { xs: 11, md: 12 }, color: 'rgba(255,255,255,0.6)', mt: 0.25 }}>{sub}</Typography>
      )}
    </Box>
  );
}
